import { attr, belongsTo, hasMany } from "@ember-data/model";
import dayjs from "dayjs";
import {
  validateDate,
  validateInclusion,
  validatePresence
} from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type DmpBulkDownloadSessionStartedJournalEvent from "./dmp-bulk-download-session-started-journal-event";
import type DmpDocumentsMetadataRetrievedJournalEvent from "./dmp-documents-metadata-retrieved-journal-event";
import type DmpRetrievalStartedJournalEvent from "./dmp-retrieval-started-journal-event";
import type DocumentRetrievedJournalEvent from "./document-retrieved-journal-event";
import type InsRetrievedJournalEvent from "./ins-retrieved-journal-event";
import type ReimbursementsRetrievedJournalEvent from "./reimbursements-retrieved-journal-event";
import type CnamProcessEndedJournalEvent from "./cnam-process-ended-journal-event";
import type DmpRetrievalSuccessfulJournalEvent from "./dmp-retrieval-successful-journal-event";

//---- Automatically generated, don't update this part ----

type OperationTypeStringEnum = "dmp" | "insi";

export default class CnamProcessStartedJournalEvent extends JournalEvent {
  static override type = "cnam-process-started-journal-event";

  /**
   * The name of the CNAM adapter used for those timed-transactions
   */
  @attr("string") declare cnamAdapterName: string;

  @attr("string") declare operationType: OperationTypeStringEnum;

  @belongsTo("cnam-process-ended-journal-event", {
    async: false,
    inverse: "cnamProcessStartedJournalEvent"
  })
  declare cnamProcessEndedJournalEvent: CnamProcessEndedJournalEvent;

  @belongsTo("reimbursements-retrieved-journal-event", {
    async: false,
    inverse: "cnamProcessStartedJournalEvent"
  })
  declare reimbursementsRetrievedJournalEvent: ReimbursementsRetrievedJournalEvent;

  @belongsTo("dmp-documents-metadata-retrieved-journal-event", {
    async: false,
    inverse: "cnamProcessStartedJournalEvent"
  })
  declare dmpDocumentsMetadataRetrievedJournalEvent: DmpDocumentsMetadataRetrievedJournalEvent;

  @belongsTo("dmp-retrieval-started-journal-event", {
    async: false,
    inverse: "cnamProcessStartedJournalEvent"
  })
  declare dmpRetrievalStartedJournalEvent: DmpRetrievalStartedJournalEvent;

  @belongsTo("dmp-retrieval-successful-journal-event", {
    async: false,
    inverse: "cnamProcessStartedJournalEvent"
  })
  declare dmpRetrievalSuccessfulJournalEvent: DmpRetrievalSuccessfulJournalEvent;

  @belongsTo("dmp-bulk-download-session-started-journal-event", {
    async: false,
    inverse: "cnamProcessStartedJournalEvents"
  })
  declare dmpBulkDownloadSessionStartedJournalEvent: DmpBulkDownloadSessionStartedJournalEvent;

  @belongsTo("ins-retrieved-journal-event", {
    async: false,
    inverse: "cnamProcessStartedJournalEvent"
  })
  declare insRetrievedJournalEvent: InsRetrievedJournalEvent;

  @hasMany("document-retrieved-journal-event", {
    async: false,
    inverse: "cnamProcessStartedJournalEvent"
  })
  declare documentRetrievedJournalEvents: DocumentRetrievedJournalEvent[];

  static override validations(intl: IntlService) {
    return {
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.cnam-process-started-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ],
      operationType: validateInclusion({
        list: ["dmp", "insi"],
        allowBlank: true,
        description: intl.t(
          "validations.cnam-process-started-journal-event.operation-type"
        )
      })
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "cnamProcessStartedJournalEvent"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "cnam-process-started-journal-event": CnamProcessStartedJournalEvent;
  }
}
